import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { 
  Trophy, Star, ChevronRight, ChevronLeft, Check, 
  Award, Building2, MapPin, Target, Lightbulb, GripVertical
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ShakersEvaluatePage() {
  const { year } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState([]);
  const [config, setConfig] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [evaluations, setEvaluations] = useState({});
  const [ranking, setRanking] = useState([]);
  const [showRanking, setShowRanking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [existingEvaluation, setExistingEvaluation] = useState(null);

  useEffect(() => {
    if (token) {
      loadData();
    }
  }, [token, year]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [candidatesRes, configRes, myEvalRes] = await Promise.all([
        fetch(`${API}/api/shakers/candidates?year=${year}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/shakers/config/${year}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/shakers/evaluations/my?year=${year}`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      if (candidatesRes.ok) {
        const data = await candidatesRes.json();
        setCandidates(data);
        // Initialize empty evaluations
        const initialEvals = {};
        data.forEach(c => {
          initialEvals[c.id] = {
            relevance: 0,
            creativity: 0,
            evidence: 0,
            collaboration: 0,
            comments: ""
          };
        });
        setEvaluations(initialEvals);
        setRanking(data.map(c => c.id));
      }

      if (configRes.ok) setConfig(await configRes.json());
      
      if (myEvalRes.ok) {
        const evalData = await myEvalRes.json();
        if (evalData && evalData.id) {
          setExistingEvaluation(evalData);
        }
      }
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Error cargando datos");
    } finally {
      setLoading(false);
    }
  };

  const updateEvaluation = (candidateId, field, value) => {
    setEvaluations(prev => ({
      ...prev,
      [candidateId]: {
        ...prev[candidateId],
        [field]: value
      }
    }));
  };

  const moveInRanking = (fromIndex, toIndex) => {
    const newRanking = [...ranking];
    const [removed] = newRanking.splice(fromIndex, 1);
    newRanking.splice(toIndex, 0, removed);
    setRanking(newRanking);
  };

  const handleNext = () => {
    const current = evaluations[candidates[currentIndex]?.id];
    if (!current?.relevance || !current?.creativity || !current?.evidence || !current?.collaboration) {
      toast.error("Por favor, evalúa todos los criterios antes de continuar");
      return;
    }
    
    if (currentIndex < candidates.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setShowRanking(true);
    }
  };

  const handlePrev = () => {
    if (showRanking) {
      setShowRanking(false);
    } else if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const candidateEvaluations = candidates.map(c => ({
        candidate_id: c.id,
        ...evaluations[c.id]
      }));

      const res = await fetch(`${API}/api/shakers/evaluations`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          year: parseInt(year),
          candidate_evaluations: candidateEvaluations,
          ranking: ranking
        })
      });

      if (res.ok) {
        toast.success("¡Evaluación enviada correctamente! Gracias por tu participación.");
        navigate("/shakers/thanks");
      } else {
        const data = await res.json();
        toast.error(data.detail || "Error enviando evaluación");
      }
    } catch (error) {
      toast.error("Error enviando evaluación");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-[#e6f5f0] flex items-center justify-center">
        <div className="animate-pulse flex items-center gap-3">
          <Trophy className="h-8 w-8 text-amber-500" />
          <span className="text-lg">Cargando...</span>
        </div>
      </div>
    );
  }

  if (existingEvaluation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-[#e6f5f0] flex items-center justify-center p-4">
        <Card className="max-w-md text-center">
          <CardContent className="p-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">¡Ya has evaluado!</h2>
            <p className="text-gray-600 mb-4">
              Gracias por tu participación en Valoriza Shakers {year}. 
              Tu evaluación fue enviada el {new Date(existingEvaluation.submitted_at).toLocaleDateString('es-ES')}.
            </p>
            <Button onClick={() => navigate("/dashboard")} className="bg-[#009B77]">
              Volver al Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!candidates.length) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-[#e6f5f0] flex items-center justify-center p-4">
        <Card className="max-w-md text-center">
          <CardContent className="p-8">
            <Trophy className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h2 className="text-xl font-bold text-gray-800 mb-2">No hay candidatos</h2>
            <p className="text-gray-600">No hay candidatos disponibles para evaluar en este momento.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentCandidate = candidates[currentIndex];
  const currentEval = evaluations[currentCandidate?.id] || {};

  // Ranking view
  if (showRanking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-[#e6f5f0] p-4 md:p-8">
        <div className="max-w-3xl mx-auto space-y-6">
          {/* Header */}
          <div className="text-center">
            <Trophy className="h-12 w-12 mx-auto text-amber-500 mb-3" />
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Ranking Final</h1>
            <p className="text-gray-600 mt-2">Ordena los candidatos de mejor a peor (arrastra para reordenar)</p>
          </div>

          {/* Ranking List */}
          <Card>
            <CardContent className="p-4">
              <div className="space-y-2">
                {ranking.map((candidateId, index) => {
                  const candidate = candidates.find(c => c.id === candidateId);
                  return (
                    <div 
                      key={candidateId}
                      className="flex items-center gap-3 p-3 bg-white border rounded-lg hover:shadow-md transition-shadow"
                    >
                      <GripVertical className="h-5 w-5 text-gray-400 cursor-move" />
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                        index === 0 ? 'bg-amber-500 text-white' :
                        index === 1 ? 'bg-gray-400 text-white' :
                        index === 2 ? 'bg-orange-400 text-white' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-800">{candidate?.name}</p>
                        <p className="text-sm text-gray-500">{candidate?.area}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          disabled={index === 0}
                          onClick={() => moveInRanking(index, index - 1)}
                        >
                          ↑
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          disabled={index === ranking.length - 1}
                          onClick={() => moveInRanking(index, index + 1)}
                        >
                          ↓
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-between">
            <Button variant="outline" onClick={handlePrev}>
              <ChevronLeft className="h-4 w-4 mr-2" />
              Volver
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={submitting}
              className="bg-[#009B77] hover:bg-[#008066]"
            >
              {submitting ? "Enviando..." : "Enviar Evaluación"}
              <Check className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Evaluation view
  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-[#e6f5f0] p-4 md:p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Trophy className="h-8 w-8 text-amber-500" />
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Valoriza Shakers {year}</h1>
          </div>
          <p className="text-gray-600">{config?.description || 'Premios Anuales de Innovación'}</p>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-center gap-2">
          {candidates.map((_, idx) => (
            <div 
              key={idx}
              className={`w-3 h-3 rounded-full transition-colors ${
                idx < currentIndex ? 'bg-[#009B77]' :
                idx === currentIndex ? 'bg-amber-500' :
                'bg-gray-300'
              }`}
            />
          ))}
        </div>

        {/* Current Candidate Card */}
        <Card className="overflow-hidden">
          <div className="bg-gradient-to-r from-[#009B77] to-[#00856A] p-6 text-white">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center text-2xl font-bold">
                {currentIndex + 1}
              </div>
              <div>
                <h2 className="text-xl md:text-2xl font-bold">{currentCandidate.name}</h2>
                <div className="flex flex-wrap gap-3 mt-2 text-sm text-white/90">
                  <span className="flex items-center gap-1">
                    <Building2 className="h-4 w-4" />
                    {currentCandidate.area}
                  </span>
                  <span className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    {currentCandidate.country}
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          <CardContent className="p-6 space-y-6">
            {/* Description */}
            <div>
              <h3 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <Award className="h-5 w-5 text-amber-500" />
                Descripción del Logro
              </h3>
              <p className="text-gray-600">{currentCandidate.description}</p>
            </div>

            {currentCandidate.reason_for_recognition && (
              <div>
                <h3 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <Target className="h-5 w-5 text-blue-500" />
                  Motivo del Reconocimiento
                </h3>
                <p className="text-gray-600">{currentCandidate.reason_for_recognition}</p>
              </div>
            )}

            {currentCandidate.value_to_company && (
              <div>
                <h3 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <Lightbulb className="h-5 w-5 text-green-500" />
                  Valor para Valoriza
                </h3>
                <p className="text-gray-600">{currentCandidate.value_to_company}</p>
              </div>
            )}

            {/* Evaluation Criteria */}
            <div className="border-t pt-6">
              <h3 className="font-semibold text-gray-700 mb-4">Evaluación</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { key: 'relevance', label: 'Relevancia y Logro', desc: 'Importancia del logro' },
                  { key: 'creativity', label: 'Creatividad e Innovación', desc: 'Nivel de innovación' },
                  { key: 'evidence', label: 'Evidencia de Resultados', desc: 'Resultados tangibles' },
                  { key: 'collaboration', label: 'Colaboración e Influencia', desc: 'Impacto en el equipo' }
                ].map(criterion => (
                  <div key={criterion.key} className="p-4 bg-gray-50 rounded-lg">
                    <p className="font-medium text-gray-700">{criterion.label}</p>
                    <p className="text-xs text-gray-500 mb-2">{criterion.desc}</p>
                    <div className="flex gap-2">
                      {[1, 2, 3].map(stars => (
                        <button
                          key={stars}
                          onClick={() => updateEvaluation(currentCandidate.id, criterion.key, stars)}
                          className={`flex items-center gap-1 px-3 py-2 rounded-lg border-2 transition-all ${
                            currentEval[criterion.key] === stars
                              ? 'border-amber-500 bg-amber-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          {[...Array(stars)].map((_, i) => (
                            <Star 
                              key={i} 
                              className={`h-5 w-5 ${
                                currentEval[criterion.key] === stars 
                                  ? 'text-amber-500 fill-amber-500' 
                                  : 'text-gray-400'
                              }`} 
                            />
                          ))}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Comments */}
            <div>
              <label className="block font-medium text-gray-700 mb-2">
                Comentarios (opcional)
              </label>
              <Textarea 
                value={currentEval.comments || ''}
                onChange={(e) => updateEvaluation(currentCandidate.id, 'comments', e.target.value)}
                placeholder="Añade cualquier observación sobre este candidato..."
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button 
            variant="outline" 
            onClick={handlePrev}
            disabled={currentIndex === 0}
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Anterior
          </Button>
          <span className="text-gray-500 self-center">
            {currentIndex + 1} de {candidates.length}
          </span>
          <Button 
            onClick={handleNext}
            className="bg-[#009B77] hover:bg-[#008066]"
          >
            {currentIndex === candidates.length - 1 ? 'Finalizar' : 'Siguiente'}
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
